import {
  useSalesServiceSalesOrdersModificationPriceCreate,
  useSalesServiceSalesOrdersModifyCreate,
  useSalesServiceSalesOrdersPartialUpdate,
  useSalesServiceSalesProductsPriceCreate
} from '@insurance/api/queries'
import {
  EditCMR,
  OrderInspectionStatus,
  OrderStatus,
  Status9e7Enum
} from '@insurance/api/requests'
import { RefundMoneyButton } from '@insurance/components'
import { useAppNavigate } from '@insurance/hooks'
import { paths } from '@insurance/routes'
import { ProductCode } from '@insurance/types'
import { replaceEmptyWithUndefined, showApiErrors } from '@shared/utils'
import { Button, Flex, Form } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { useTranslation } from 'react-i18next'

import { useCmrInsuranceValues } from './CmrInsuranceContext'
import {
  formProps,
  normalizeCmrData,
  normalizeModifyOrderData,
  normalizeOrderData,
  orderParentOrChildId,
  removeCmrValuesFromStorage
} from './utils'

type Props = {
  steps: number
  onReadyToSaveData?: () => void
}

export const CmrInsuranceControls = ({ steps, onReadyToSaveData }: Props) => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()
  const { order, isEditable } = useCmrInsuranceValues()
  const { stepKey, priceKey, orderKey } = formProps(t)

  const step = Form.useWatch(stepKey.name, { preserve: true })
  const orderId = Form.useWatch(orderKey.name, { preserve: true })

  const price = Form.useWatch(priceKey.name, { preserve: true })

  const navigate = useAppNavigate()

  const hasPrevStep = step > 0
  const isCalculateStep = step === steps - 2
  const isReadyToApprove = step === steps - 1 && !!orderId
  const isNavigationStep = !isCalculateStep && !isReadyToApprove

  const removeAllFromStorage = () => {
    removeCmrValuesFromStorage(order?.id)
    removeCmrValuesFromStorage(orderId)
  }

  const handleFinishStepError = (error: unknown) => {
    showApiErrors(error, formInstance)
  }

  const handleChangeStep = (newStep: number) => {
    formInstance.setFieldValue(stepKey.name, newStep)
    onReadyToSaveData?.()
  }

  const handlePrevStep = () => {
    const prevStep = step - 1

    if (isEditable || !orderId) handleChangeStep(prevStep)
    else
      updateOrder({
        id: orderId,
        requestBody: {
          stage: prevStep,
          status: Status9e7Enum.DATA_ENTRY
        }
      })
  }

  const handleNavigate = async () => {
    await formInstance.validateFields()
    handleChangeStep(step + 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const { mutate: updateOrder, isPending: isUpdateOrderPending } =
    useSalesServiceSalesOrdersPartialUpdate({
      onSuccess: (data, variable) => {
        const newStep = variable?.requestBody?.stage || 0
        handleChangeStep(newStep)

        if (!newStep) {
          formInstance.setFieldValue(priceKey.name, undefined)

          return
        }

        if (data?.price_data) {
          formInstance.setFieldValue(priceKey.name, {
            currency: data.price_data?.currency,
            amount: Number(Number(data.price_data?.price)?.toFixed(2))
          })
        }

        formInstance.setFieldValue(orderKey.name, data.id)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
      onError: (error: AnyObject) => {
        const err = { ...error, body: error?.body?.cmr ?? error?.body }
        handleFinishStepError(err)
      }
    })

  const { mutate: calculate, isPending: isCalculatingLoading } =
    useSalesServiceSalesProductsPriceCreate({
      onSuccess: (data) => {
        if (data.order?.id)
          updateOrder({
            id: data.order.id,
            requestBody: {
              stage: step + 1,
              status: Status9e7Enum.DATA_ENTRY
            }
          })
      },
      onError: handleFinishStepError
    })

  const { mutate: calculateModify, isPending: isCalculatingModifyLoading } =
    useSalesServiceSalesOrdersModificationPriceCreate({
      onSuccess: (data) => {
        handleChangeStep(step + 1)

        formInstance.setFieldValue(priceKey.name, {
          currency: data?.price_data?.currency,
          amount: Number(Number(data?.price_data?.price)?.toFixed(2))
        })
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
      onError: (error: AnyObject) => {
        const err = { ...error, body: { ...error?.body?.cmr, ...error?.body } }
        handleFinishStepError(err)
      }
    })

  const {
    mutate: calculateModifyRefund,
    isPending: isCalculatingModifyRefundLoading
  } = useSalesServiceSalesOrdersModificationPriceCreate({
    onSuccess: (data) => {
      formInstance.setFieldValue(priceKey.name, {
        currency: data?.price_data?.currency,
        amount: Number(Number(data?.price_data?.price)?.toFixed(2))
      })
    },
    onError: handleFinishStepError
  })

  const { mutateAsync: modifyOrder, isPending: isModifyOrderLoading } =
    useSalesServiceSalesOrdersModifyCreate({
      onSuccess: (order) => {
        formInstance.setFieldValue(orderKey.name, order.id)
      },
      onError: handleFinishStepError
    })

  const handleCalculateRefund = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)
    const bodyValues = normalizeModifyOrderData(values)

    if (order?.id)
      calculateModifyRefund({ requestBody: bodyValues!, id: order?.id })
  }

  const handleOnlyModify = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    if (!order?.id) return

    const id =
      order.status === OrderStatus.REJECTED || !orderParentOrChildId(order)
        ? order?.parent?.id || order.id
        : order.id

    const bodyValues = normalizeModifyOrderData(values)
    modifyOrder(
      {
        id,
        requestBody: replaceEmptyWithUndefined({
          ...bodyValues,
          status: OrderInspectionStatus.OFFER_READY
        })
      },
      {
        onSuccess: (order) => {
          removeAllFromStorage()
          navigate(
            order.id ? paths.orders.details(order.id) : paths.salesPanel.root
          )
        }
      }
    )
  }

  const handleCalculate = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    if (order?.id) {
      const bodyValues = normalizeModifyOrderData(values)

      if (
        orderParentOrChildId(order) &&
        (!order?.child || order?.child?.status === OrderStatus.CANCELED)
      )
        await modifyOrder({
          id: order?.id,
          requestBody: replaceEmptyWithUndefined({
            ...bodyValues,
            status: OrderInspectionStatus.DATA_ENTRY
          })
        })

      return calculateModify({
        id: order.id,
        requestBody: replaceEmptyWithUndefined(bodyValues)
      })
    }

    if (values?.order_id) {
      const editValues = normalizeCmrData(values)
      updateOrder({
        id: values.order_id,
        requestBody: {
          stage: step + 1,
          status: Status9e7Enum.DATA_ENTRY,
          cmr: replaceEmptyWithUndefined(editValues) as EditCMR
        }
      })
    } else {
      const bodyValues = normalizeCmrData(values)
      calculate({ code: ProductCode.CMR, requestBody: bodyValues })
    }
  }

  const handleReadyToReview = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    if (order?.id) {
      const bodyValues = await normalizeModifyOrderData(values)

      const id =
        order.status === OrderStatus.REJECTED || !orderParentOrChildId(order)
          ? order?.parent?.id || order.id
          : order.id

      return modifyOrder(
        {
          id,
          requestBody: replaceEmptyWithUndefined({
            ...bodyValues,
            status: OrderInspectionStatus.OFFER_READY
          })
        },
        {
          onSuccess: (order) => {
            removeAllFromStorage()
            navigate(
              order.id ? paths.orders.details(order.id) : paths.salesPanel.root
            )
          }
        }
      )
    }

    const bodyValues = normalizeOrderData(values)

    updateOrder(
      {
        id: orderId,
        requestBody: {
          stage: step,
          status: Status9e7Enum.OFFER_READY,
          cmr: bodyValues
        }
      },
      {
        onSuccess: (order) => {
          removeAllFromStorage()
          navigate(
            order?.id ? paths.orders.details(order.id) : paths.salesPanel.root
          )
        }
      }
    )
  }

  const orderPrice = {
    amount: order?.full_price ? Number(order?.full_price) : 0
  }

  const canRefund = isEditable && orderPrice?.amount > price?.amount

  return (
    <Flex justify="end" gap={12}>
      {hasPrevStep && (
        <Button
          color="primary"
          variant="outlined"
          loading={isUpdateOrderPending}
          onClick={handlePrevStep}
        >
          {t('prev_step')}
        </Button>
      )}

      {isNavigationStep && (
        <Button type="primary" onClick={handleNavigate}>
          {t('next_step')}
        </Button>
      )}

      {isCalculateStep && (
        <Button
          type="primary"
          loading={
            isCalculatingLoading ||
            isUpdateOrderPending ||
            isCalculatingModifyLoading
          }
          onClick={handleCalculate}
        >
          {t('calculate')}
        </Button>
      )}

      {!canRefund && isReadyToApprove && (
        <Button
          type="primary"
          loading={isUpdateOrderPending || isModifyOrderLoading}
          onClick={handleReadyToReview}
        >
          {t('to_approve')}
        </Button>
      )}

      {canRefund && isReadyToApprove && (
        <RefundMoneyButton
          loading={isCalculatingModifyRefundLoading}
          disabledSelect
          onChangeRefund={handleCalculateRefund}
          onRefund={handleOnlyModify}
        />
      )}
    </Flex>
  )
}
